import React, { ChangeEvent, FormEventHandler, useCallback, useState } from "react"
import { Button, Col, Container, Form, Row } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useGA4Event } from '../hooks/useGA4Event'
import { Turnstile } from '@marsidev/react-turnstile'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ""
const isTurnstileEnabled = !!TURNSTILE_SITE_KEY

const gFormInputIds = {
  category: "689339063",
  name: "2043954811",
  kana: "257148033",
  email: "416976063",
  phone: "513308762",
  content: "1323439905"
} as const

const categories = [
  {
    "displayName": "CATEGORY_OPPORTUNITY",
    "innerName": "商談"
  },
  {
    "displayName": "CATEGORY_OTHERS",
    "innerName": "その他"
  }
] as const

type CategoryDisplayNames = typeof categories[number]["displayName"]

type FormData = {
  [K in keyof typeof gFormInputIds]: string;
}

type Props = {
  category?: CategoryDisplayNames
}

export const Contact: React.FC<Props> = (props) => {
  const { t } = useTranslation("translations", { keyPrefix: "CONTACT" })
  const { sendEvent } = useGA4Event()
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const shouldCategorySelectBeDisabled = props.category !== undefined
  const [formData, setFormData] = useState<FormData>(() => {
    const category = shouldCategorySelectBeDisabled ? categories.find(c => c.displayName === props.category)?.innerName : categories[0].innerName;
    return {
      category: category || categories[0].innerName,
      name: "",
      kana: "",
      email: "",
      phone: "",
      content: ""
    };
  });

  const handleValueChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const fieldName = event.target.getAttribute("name") as keyof FormData
    const value = event.currentTarget.value

    setFormData((data) => ({...data, [fieldName]: value}))
  }

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token)
  }, [])

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null)
  }, [])

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (!event.currentTarget.checkValidity()) {
      return
    }

    if (isTurnstileEnabled && !turnstileToken) {
      return
    }

    const entries = Object.entries(formData).reduce<Record<string, string>>(
      (params, [k, v]) => ({
        ...params,
        [`entry.${gFormInputIds[k as keyof typeof gFormInputIds]}`]: v,
      }),
      { "form-name": "contact" }
    )

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...entries,
          "cf-turnstile-response": turnstileToken,
        }),
      })

      if (!res.ok) {
        throw new Error("submission failed")
      }

      sendEvent('form_submit', {
        form_type: 'contact',
        category: formData.category,
      })

      window.location.href = "/thanks"
    } catch (error) {
      console.error('Form submission failed:', error)
    }
  }


  return (
    <div id="contact" style={{backgroundColor: "lightgray"}}>
      <Container>
        <article className="py-5">
          <div className="text-center mb-4">
            <h1 className="mb-0">CONTACT</h1>
            <small>{t("SUBTITLE")}</small>
          </div>
            <Row>
              <Col xl={12} className="text-center mb-3">
                <p>{t("DESC")}</p>
              </Col>
            </Row>
          <Form onSubmit={handleSubmit} className="w-100">
            <input type="hidden" name="form-name" value="contact" />
            <Row className="mt-4">
              <Col xl={5}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between"><p className="mb-0">{t("LABEL_CATEGORY")}</p><small className="text-end bg-dark text-light py-1 px-2">{t("LABEL_REQUIRED")}</small></Form.Label>
                  <Form.Select name="category" value={formData.category} onChange={handleValueChange} as="select" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} required disabled={shouldCategorySelectBeDisabled}>
                    {categories.map(c => (
                      <option key={c.innerName} value={c.innerName}>{t(c.displayName)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between"><p className="mb-0">{t("LABEL_NAME")}</p><small className="text-end bg-dark text-light py-1 px-2">{t("LABEL_REQUIRED")}</small></Form.Label>
                  <Form.Control name="name" value={formData.name} onChange={handleValueChange} type="text" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t("LABEL_KANA")}</Form.Label>
                  <Form.Control name="kana" value={formData.kana} onChange={handleValueChange} type="text" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between"><p className="mb-0">{t("LABEL_EMAIL")}</p><small className="text-end bg-dark text-light py-1 px-2">{t("LABEL_REQUIRED")}</small></Form.Label>
                  <Form.Control name="email" value={formData.email} onChange={handleValueChange} type="email" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t("LABEL_PHONE_NUMBER")}</Form.Label>
                  <Form.Control name="phone" value={formData.phone} onChange={handleValueChange} type="text" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} />
                </Form.Group>
              </Col>
              <Col xl={7} className="d-flex flex-column">
                <Form.Group className="d-flex flex-column flex-grow-1">
                  <Form.Label className="d-flex">
                    <p className="mb-0">{t("LABEL_CONTENT")}</p>
                    <small className="text-end bg-dark text-light py-1 px-2 ms-auto">{t("LABEL_REQUIRED")}</small>
                  </Form.Label>
                  <Form.Control name="content" value={formData.content} onChange={handleValueChange} as="textarea" className="border-0 rounded-0 flex-grow-1" style={{backgroundColor: "rgb(241, 242, 246)"}} required />
                </Form.Group>
              </Col>
            </Row>
            {isTurnstileEnabled && (
              <div className="d-flex justify-content-center mt-3">
                <Turnstile siteKey={TURNSTILE_SITE_KEY} onSuccess={handleTurnstileVerify} onExpire={handleTurnstileExpire} />
              </div>
            )}
            <Row className="mt-3">
              <Col xl={{ span: 6, offset: 3 }}>
                <Button variant="success" className="w-100 border-0 rounded-0 text-light" type="submit" disabled={isTurnstileEnabled && !turnstileToken}>{t("BUTTON_SUBMIT")}</Button>
              </Col>
            </Row>
          </Form>
        </article>
      </Container>
    </div>
  )
}
